'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { isAdSenseBlockedHost } from '@/lib/ad-localhost';
import { isFramed } from '@/lib/is-framed';
import { requiresAdConsent } from 'shared/src/types/region.type';

const AD_CLIENT_ID = 'ca-pub-4839450211797240';

const BOT_PATTERN =
  /bot|crawl|spider|scrape|headless|phantom|puppeteer|selenium|webdriver|preview|slurp|bingpreview|chatgpt|gpt-|claude|anthropic|perplexity|cohere|facebook|twitter|linkedin|whatsapp|telegram|slack|discord/i;

function getAdvertisingConsent(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stored = localStorage.getItem('cookie-consent-preferences');
    if (!stored) return false;
    const parsed = JSON.parse(stored);
    return parsed.preferences?.advertising === true;
  } catch {
    return false;
  }
}

const GoogleAdsense = ({
  isDevMode = false,
  regionCode,
}: {
  isDevMode?: boolean;
  regionCode?: string;
}) => {
  const [hasConsent, setHasConsent] = useState(false);
  const [isClient, setIsClient] = useState(false);

  const consentRequired = requiresAdConsent(regionCode);

  useEffect(() => {
    if (typeof navigator !== 'undefined' && BOT_PATTERN.test(navigator.userAgent)) return;
    // Framed (the extension's side panel): AdSense does not allow ads inside extensions.
    if (isFramed()) return;
    setIsClient(true);
    setHasConsent(getAdvertisingConsent());

    const handleConsentChange = (event: CustomEvent) => {
      setHasConsent(event.detail?.advertising === true);
    };

    window.addEventListener('cookiePreferencesChanged', handleConsentChange as EventListener);
    return () => window.removeEventListener('cookiePreferencesChanged', handleConsentChange as EventListener);
  }, []);

  const hasValidConsent = consentRequired ? hasConsent : true;
  const shouldLoad =
    isClient &&
    (process.env.NODE_ENV === 'production' || isDevMode) &&
    (hasValidConsent || isDevMode);

  if (!shouldLoad) return null;
  if (typeof window !== 'undefined' && isAdSenseBlockedHost(window.location.hostname)) return null;

  // next/script `lazyOnload` yields the ad loader to hydration (it fires during
  // browser idle after load) instead of the old raw `document.head.appendChild`,
  // which competed with first-paint/hydration. The stable `id` dedupes automatically,
  // so no manual `querySelector` guard is needed.
  return (
    <Script
      id="adsbygoogle-init"
      strategy="lazyOnload"
      src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${AD_CLIENT_ID}`}
      crossOrigin="anonymous"
    />
  );
};

export default GoogleAdsense;
